import { DeleteTeamDialog, InviteMemberDialog, TeamCard, TeamDialog } from '@/components/teams';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import { useAuth } from '@/hooks/useAuth';
import type { AppContext } from '@/main';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@ui/components/button';
import type { Organization } from 'better-auth/plugins/organization';
import { Plus, Users } from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_dashboard/teams/')<AppContext>({
  component: RouteComponent,
  loader: () => ({
    crumb: 'Teams',
  }),
});

function RouteComponent() {
  const { useListOrganizations, setActive } = useAuth();
  const { data: teams, isPending } = useListOrganizations();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Organization | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    teamId: string | null;
    teamName: string;
  }>({
    open: false,
    teamId: null,
    teamName: '',
  });
  const [inviteDialog, setInviteDialog] = useState<{
    open: boolean;
    teamId: string | null;
  }>({
    open: false,
    teamId: null,
  });

  console.log('Teams:', teams);

  const handleEdit = (team: Organization) => {
    setEditingTeam(team);
  };

  const handleDelete = (teamId: string) => {
    const team = teams?.find((t) => t.id === teamId);
    if (team) {
      setDeleteDialog({
        open: true,
        teamId,
        teamName: team.name,
      });
    }
  };

  const handleInviteMember = (team: Organization) => {
    setInviteDialog({
      open: true,
      teamId: team.id,
    });
  };

  const handleCreateTeam = () => {
    setIsCreateDialogOpen(true);
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <PageHeaderTitle>Teams</PageHeaderTitle>
          <PageHeaderDescription>Manage your teams and their members</PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button onClick={handleCreateTeam} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Team
          </Button>
        </PageHeaderActions>
      </PageHeader>

      {isPending ? (
        <div>Loading...</div>
      ) : teams ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onInviteMember={handleInviteMember}
              onSetActive={() =>
                setActive({
                  organizationId: team.id,
                })
              }
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Users className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No teams yet</h3>
          <p className="text-muted-foreground mb-4">Create your first team to get started</p>
          <Button onClick={handleCreateTeam} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Team
          </Button>
        </div>
      )}

      <TeamDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      <TeamDialog
        open={!!editingTeam}
        onOpenChange={(open) => !open && setEditingTeam(null)}
        team={editingTeam}
      />

      <DeleteTeamDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        teamId={deleteDialog.teamId}
        teamName={deleteDialog.teamName}
      />

      <InviteMemberDialog
        open={inviteDialog.open}
        onOpenChange={(open) => setInviteDialog((prev) => ({ ...prev, open }))}
        organizationId={inviteDialog.teamId}
      />
    </Page>
  );
}
